import { Timer, BarChart3, Trophy, User, ShoppingBag, Users, Shield } from "lucide-react";
import { useLocation, Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useFeatureFlags } from "@/contexts/FeatureFlagsContext";
import { useUser } from "@/contexts/user";
import { useState, useEffect } from "react";
import { debug } from "@/utils/debug";

const conditionalNavItems = [
  { icon: ShoppingBag, label: "Shop", path: "/shop", feature: "shopEnabled" as const },
  { icon: Users, label: "Groups", path: "/groups", feature: "groupsEnabled" as const },
];

export function Navigation() {
  const location = useLocation();
  const { isFeatureEnabled } = useFeatureFlags();
  const { user, loading } = useUser();

  // Track focus mode and session complete overlay states reactively
  const [hideNav, setHideNav] = useState(() => 
    sessionStorage.getItem('focusMode') === 'true' || 
    sessionStorage.getItem('sessionCompleteOverlay') === 'true'
  );
  
  // Listen for focus mode and overlay changes
  useEffect(() => {
    const checkHideNav = () => {
      const inFocusMode = sessionStorage.getItem('focusMode') === 'true';
      const showingOverlay = sessionStorage.getItem('sessionCompleteOverlay') === 'true';
      setHideNav(inFocusMode || showingOverlay);
    };
    
    // Check initially
    checkHideNav();
    
    // Poll for changes (fallback)
    const interval = setInterval(checkHideNav, 500);
    
    // Listen to storage events (for cross-tab changes)
    window.addEventListener('storage', checkHideNav);
    
    // Listen for immediate hide nav updates from Timer
    window.addEventListener('hideNavUpdate', checkHideNav);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', checkHideNav);
      window.removeEventListener('hideNavUpdate', checkHideNav);
    };
  }, []);

  const onBreakPage = location.pathname === '/rest' || location.pathname === '/rest/complete';
  const onMarketingPage = location.pathname === '/features' || location.pathname === '/pricing';
  // V2: Suppress navigation during onboarding to prevent "restart onboarding" vector
  const onOnboardingPage = location.pathname.startsWith('/onboarding');
  
  // Build nav items in order: Timer → Dashboard → Leaderboard (if social AND has studied) → Profile → Admin (if admin)
  // Debug: Log navigation visibility conditions
  const showLeaderboard = !loading && user?.study_profile === 'social';
  
  if (!loading) {
    debug.log('[Navigation] Leaderboard visibility check:', {
      study_profile: user?.study_profile,
      first_study_done: user?.first_study_done,
      showLeaderboard,
      loading
    });
  }
  
  const availableNavItems = [
    { icon: Timer, label: "Timer", path: "/timer" },
    { icon: BarChart3, label: "Dashboard", path: "/dashboard" },
    ...(showLeaderboard ? [{ icon: Trophy, label: "Leaderboard", path: "/leaderboard" }] : []),
    { icon: User, label: "Profile", path: "/profile" },
    ...(loading ? [] : conditionalNavItems.filter(item => isFeatureEnabled(item.feature)))
  ];

  // Don't render navigation in focus mode, session complete overlay, break pages, marketing pages, or onboarding
  if (hideNav || onBreakPage || onMarketingPage || onOnboardingPage) {
    return null;
  }

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-card border-t border-border shadow-card z-[9999]">
      <div className="flex items-center justify-between h-16 max-w-md mx-auto px-4">
        <div className="flex items-center justify-around flex-1">
          {availableNavItems.map(({ icon: Icon, label, path }) => {
            const isActive = location.pathname === path;
            const testId = `nav-link-${label.toLowerCase().replace(/\s+/g, '-')}`;
            return (
              <Link
                key={path}
                to={path}
                data-testid={testId}
                className={cn(
                  "flex flex-col items-center justify-center py-2 transition-smooth",
                  isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Icon 
                  size={22} 
                  className={cn(
                    "transition-smooth",
                    isActive && "animate-bounce-subtle"
                  )} 
                />
                <span className="text-xs font-medium mt-1">{label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
}